import React, { useState, useEffect, useRef } from "react";
import { Formik, Form, Field } from "formik";
import { Input, Button, Row, Col, Card, CardBody } from "reactstrap";
import { get, put, del } from "../../../api/axios";
import endpoints from "../../../api/endpoints";
import { toast, Bounce } from "react-toastify";
import * as Yup from "yup";
import { useSelector, useDispatch } from "react-redux";
import { setUser } from "../../../store/slices/authSlice";
import cameraIcon from "../../../assets/images/camera.png";
import placeholderImage from "../../../assets/images/bigPlaceholder.svg";

const ProfileSettings = () => {
  document.title = "Profile Settings | Elevated - Admin Dashboard";
  const userId = useSelector((state) => state.auth.currentUser?.id);
  console.log("id user", userId);
  const dispatch = useDispatch();
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phoneNumber: "",
    additionalInfo: { profileImage: "" },
  });
  const [file, setFile] = useState(null);
  const [isImageRemoved, setIsImageRemoved] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (userId) {
        try {
          const response = await get(`${endpoints.user}/${userId}`);
          if (response?.status === 200 && response?.data?.data) {
            setProfileData(response.data.data);
          } else {
            // toast.error("Failed to load profile data");
          }
        } catch (error) {
          toast.error("Error fetching profile data");
        }
      }
    };
    fetchProfileData();
  }, [userId]);

  const handleImageChange = (event) => {
    const selectedFile = event.target.files[0];
    if (selectedFile) {
      setFile(selectedFile);
      setIsImageRemoved(false);
    }
  };

  const handleRemoveImage = async () => {
    setFile(null);
    setIsImageRemoved(true);

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
    await removeMedia();
    setProfileData((prevState) => ({
      ...prevState,
      additionalInfo: { ...prevState.additionalInfo, profileImage: "" },
    }));
  };

  const removeMedia = async () => {
    if (!profileData?.additionalInfo?.profileImage) return;

    const fileToDelete = profileData.additionalInfo.profileImage;

    try {
      const response = await del(
        `${endpoints.gym}/del?resourceType=user&resourceId=${userId}&url=${fileToDelete}`
      );

      if (response?.status === 200) {
      } else {
        toast.error("Failed to remove image");
      }
    } catch (error) {
      toast.error("Error removing image");
    }
  };

  const handleUpdateProfile = async (values) => {
    const name = values.fullName.split(" ");
    const updatedData = {
      firstName: name[0],
      lastName: name?.[1] || "",
      email: values.email,
      phoneNumber: values.phoneNumber,
    };

    const formData = new FormData();
    Object.keys(updatedData).forEach((key) => {
      formData.append(key, updatedData[key]);
    });

    if (file && !isImageRemoved) {
      formData.append("file", file);
    } else if (isImageRemoved) {
      formData.append("removeImage", true);
    }

    try {
      const response = await put(`${endpoints.user}/${userId}`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      });

      if (response?.status === 200) {
        toast.success("Profile Updated Successfully", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
        const updatedResponse = await get(`${endpoints.user}/${userId}`);
        setProfileData(updatedResponse?.data?.data);
        dispatch(setUser(response?.data?.data));
        setIsEditing(false);
        setIsImageRemoved(false);
      } else {
        toast.error("Failed to Update Profile", {
          position: "bottom-center",
          theme: "colored",
          transition: Bounce,
        });
      }
    } catch (error) {
      toast.error("Error updating profile");
    }
  };

  const validationSchema = Yup.object({
    fullName: Yup.string().required("Name is required"),
    email: Yup.string()
      .email("Invalid email address")
      .required("Email is required"),
    phoneNumber: Yup.string()
      .matches(
        /^\d{11}$/,
        "Phone number must be 11 digits and contain only numbers"
      )
      .required("Phone number is required"),
  });

  const renderProfileImage = () => {
    const imageUrl =
      file && !isImageRemoved
        ? URL.createObjectURL(file)
        : isImageRemoved
        ? placeholderImage
        : profileData?.additionalInfo?.profileImage
        ? `${process.env.REACT_APP_IMG_URL}${profileData.additionalInfo.profileImage}`
        : placeholderImage;

    const isImagePresent =
      (file && !isImageRemoved) ||
      (!isImageRemoved && profileData?.additionalInfo?.profileImage);

    return (
      <div className="position-relative d-inline-block">
        <img
          src={imageUrl}
          alt="Profile"
          className="object-fit-cover rounded-circle position-relative"
          style={{
            height: "160px",
            width: "160px",
          }}
        />
        {isEditing && (
          <button
            className="rounded-5"
            style={{
              fontSize: "12px",
              color: "black",
              cursor: "pointer",
              zIndex: 1,
              position: "absolute",
              top: 10,
              right: 15,
            }}
            onClick={
              isImagePresent
                ? handleRemoveImage
                : () => fileInputRef.current.click()
            }
          >
            {isImagePresent ? (
              "X"
            ) : (
              <img
                src={cameraIcon}
                alt="Camera Icon"
                style={{
                  height: "32px",
                  width: "32px",
                  border: "0px",
                }}
              />
            )}
          </button>
        )}
        <input
          type="file"
          accept="image/*"
          ref={fileInputRef}
          className="d-none"
          onChange={handleImageChange}
        />
      </div>
    );
  };

  return (
    <>
      <Card className="rounded-4" style={{ minHeight: "550px" }}>
        <CardBody>
          {/* Render content */}{" "}
          <Row className="g-0">
            <Col xs="12">
              <h2 className="card-title fw-bold ff-sora font-size-20 primary-black">
                Profile Settings
              </h2>
              <span className="ff-sora secondary-black fw-normal font-size-16">
                Profile Media
              </span>
            </Col>
          </Row>
          <Row className="g-0">
            <Col xs="12" className="mb-3">
              {renderProfileImage()}
            </Col>
          </Row>
          <Row className="g-0">
            <Formik
              initialValues={{
                fullName: `${profileData.firstName} ${
                  profileData.lastName || ""
                }`,
                email: profileData.email,
                phoneNumber: profileData.phoneNumber || "NA",
              }}
              enableReinitialize
              validationSchema={validationSchema}
              onSubmit={handleUpdateProfile}
            >
              {({ values, handleChange, errors, touched }) => (
                <Form>
                  <Row className="align-items-center g-0">
                    <Col
                      xs={6}
                      className="light-black font-size-16 ff-sora fw-normal"
                    >
                      <p>Name</p>
                      <p>Email</p>
                      <p>Phone No.</p>
                    </Col>
                    <Col
                      xs={6}
                      className="secondary-black font-size-16 ff-sora fw-normal"
                    >
                      {isEditing ? (
                        <>
                          <Field
                            as={Input}
                            type="text"
                            name="fullName"
                            value={values.fullName}
                            onChange={handleChange}
                            className="mb-2"
                          />
                          {errors.fullName && touched.fullName && (
                            <div className="text-danger">{errors.fullName}</div>
                          )}
                          <Field
                            as={Input}
                            type="email"
                            name="email"
                            value={values.email}
                            onChange={handleChange}
                            className="mb-2"
                          />
                          {errors.email && touched.email && (
                            <div className="text-danger">{errors.email}</div>
                          )}
                          <Field
                            as={Input}
                            type="text"
                            name="phoneNumber"
                            value={values.phoneNumber}
                            onChange={handleChange}
                          />
                          {errors.phoneNumber && touched.phoneNumber && (
                            <div className="text-danger">
                              {errors.phoneNumber}
                            </div>
                          )}
                        </>
                      ) : (
                        <>
                          <p>
                            {profileData.firstName}{" "}
                            {profileData?.lastName || ""}
                          </p>
                          <p>
                            {profileData.email
                              ? `${profileData.email.substring(0, 12)}...`
                              : ""}
                          </p>

                          <p>{profileData.phoneNumber || "NA"}</p>
                        </>
                      )}
                    </Col>
                  </Row>

                  {isEditing && (
                    <Row className="g-0">
                      <Col xs="12" className="mt-3 text-end">
                        <Button type="submit" className="rounded-pill">
                          Update Profile
                        </Button>
                      </Col>
                    </Row>
                  )}
                </Form>
              )}
            </Formik>
          </Row>
          {!isEditing && (
            <Row className="g-0">
              <Col xs="12" className="mt-3 text-end">
                <Button
                  onClick={() => setIsEditing(true)}
                  className="rounded-pill"
                >
                  Edit Profile
                </Button>
              </Col>
            </Row>
          )}
        </CardBody>
      </Card>
      <div
        className="d-flex justify-content-between align-items-center"
        style={{
          height: "60px",
          position: "sticky",
          bottom: "0",
          zIndex: "10",
        }}
      ></div>
    </>
  );
};

export default ProfileSettings;
